/**
 * This is used for diffing between url params and
 * active filters for client-side managed product type filters
 * that have `[data-no-menu-filter-match]`.
 * @typedef {ProductTypeDiffAdd | ProductTypeDiffRemove} ProductTypeDiff
 */

/**
 * @typedef {Object} ProductTypeDiffAdd
 * @property {'add'} action - Action todo
 * @property {string} paramValue - Url param value
 * @property {never} filter - FacetRemove component
 */

/**
 * @typedef {Object} ProductTypeDiffRemove
 * @property {'remove'} action - Action todo
 * @property {never} paramValue - Url param value
 * @property {FacetRemove} filter - FacetRemove component
 */

class FacetFiltersForm extends HTMLElement {

  isFacetFiltersDesktop = false;
  isFacetFiltersMobile = false;

  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm !== null) {
      const desktopId = 'FacetFiltersForm';
      const mobileId = 'FacetFiltersFormMobile';
      this.isFacetFiltersDesktop = (facetForm.id === desktopId);
      this.isFacetFiltersMobile = (facetForm.id === mobileId);
    }

    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    this.updateMismatchProductTypes();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
      const forms = document.querySelectorAll('facet-filters-form');
      forms.forEach((form) => {
        form.updateMismatchProductTypes();
      });
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);

        const forms = document.querySelectorAll('facet-filters-form');
        forms.forEach((form) => form.updateMismatchProductTypes());
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const child = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
          if (child) child.before(elementToRender);
          else {
            document.querySelector(
              `#${elementToRender.parentElement.id}`
            ).append(elementToRender);
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  /**
   * Sets up functionality for productType filters
   * where the liquid menu.links link and filters.filter value
   * didn't have a match. These have `[data-no-menu-filter-match]` attr
   * in the dom and are the input elements. facet-remove placed by this
   * function will also have data-no-menu-filter-match.
   */
  updateMismatchProductTypes() {

    if (
      !this.isFacetFiltersDesktop &&
      !this.isFacetFiltersMobile
    ) return;

    const countContainer = document.getElementById('ProductCount');
    if (!countContainer) return;
    // Disables this function when there's a no results page
    // as the default rendering method empties the active filters block.
    if (countContainer && countContainer.textContent[0] === '0') return;

    const params = new URLSearchParams(window.location.search);
    const filterInputName = 'filter.p.m.custom.product_type';
    const productTypeParams = params.getAll(filterInputName);

    const containerSelector = (
      this.isFacetFiltersDesktop
        ? '#FacetsWrapperDesktop > .active-facets-desktop'
        : '.active-facets-mobile'
    );

    const activeFilterContainer = document.querySelector(containerSelector);
    if (!activeFilterContainer) {
      const err = new Error("Couldn't find filter container");
      console.error(err);
      return;
    }

    let activeFilters = [];

    if (this.isFacetFiltersDesktop) {
      /** @type {NodeListOf<FacetRemove> | null} */
      activeFilters = this.querySelectorAll(
        `facet-remove[data-param-name="${filterInputName}"]`,
      );
    }

    if (this.isFacetFiltersMobile) {
      /** @type {NodeListOf<FacetRemove> | null} */
      activeFilters = document.querySelectorAll(
        `.active-facets-mobile > facet-remove[data-param-name="${filterInputName}"]`,
      );
    }

    const filtersDiff = this.diffProductTypeFilters(productTypeParams, activeFilters);

    for (const diff of filtersDiff) {

      if (diff.action === 'remove') {
        diff.filter.remove();
        this.updateInputChecked(
          filterInputName,
          diff.paramValue,
          false,
        );
      }

      if (diff.action === 'add') {
        const facet = this.createProductTypeFacetRemove(diff.paramValue);

        if (facet && this.isFacetFiltersMobile) {
          const resetFiltersBtn = activeFilterContainer.lastElementChild;
          if (resetFiltersBtn) {
            activeFilterContainer.insertBefore(facet, resetFiltersBtn)
          } else {
            activeFilterContainer.append(facet);
          }
        }

        if (facet && this.isFacetFiltersDesktop) {
          activeFilterContainer.append(facet);
        }

        this.updateInputChecked(
          filterInputName,
          diff.paramValue,
          true,
        );
      }
    }
  }

  /**
   * Updates inputs state between checked/unchecked
   * and handles necessary side-effects.
   * @param {string} name - Input [name] attr
   * @param {string} value - Input [value] attr
   * @param {boolean} isChecked - Whether to set checked true/false
   */
  updateInputChecked(name, value, isChecked) {

    /** @type {HTMLInputElement | null} */
    const input = this.querySelector(
      `input[name="${name}"][value="${value}"]`
    );
    if (!input) return;

    const parent = input.parentElement;
    const editParent = (
      parent.tagName.toLowerCase() === 'label'
    );

    if (isChecked) {
      input.setAttribute('checked', '');
      input.checked = true;
      if (editParent) parent.classList.add('active');
      this.updateProductTypeActiveCount(1);
    } else {
      input.removeAttribute('checked');
      input.checked = false;
      if (editParent) parent.classList.remove('active');
      this.updateProductTypeActiveCount(-1);
    }
  }

  /**
   * Updates the dropdown headers containing
   * the number of active product type filters active
   * @param {number} increaseBy
   */
  updateProductTypeActiveCount(increaseBy) {

    // doesn't appear to be an active facet count
    // on the mobile menu atm
    if (this.isFacetFiltersMobile) return;

    if (this.isFacetFiltersDesktop) {
      const dropHeader = this.querySelector(
        '[id^="Details-filter.p.m.custom.product_type"]',
      );
      if (!dropHeader) {
        const err = new Error(
          "Details dropdown for 'Filter by Product Type' not found",
        );
        console.error(err);
        return;
      }
      const countWrapper = dropHeader.querySelector('.facets__selected');
      if (!countWrapper) {
        const err = new Error(
          "Active filter count for 'Filter by Product Type' not found",
        );
        console.error(err);
        return;
      }

      this.#updateCount(countWrapper, increaseBy);
    }
  }

  /**
   * Updates (1) to (2) for example
   * @param {HTMLElement} element - The element containing the count text
   * @param {number} increaseBy - Num to increase the count (negative num to decrease)
   */
  #updateCount(element, increaseBy) {
    const text = (
      element.innerText
        .replace('(', '')
        .replace(')', '')
    );
    let num = parseInt(text);
    if (isNaN(num)) {
      const err = new Error(
        `Couldn't parse number string to number`,
      );
      console.error(err, text);
      return;
    }

    num += increaseBy;
    if (num < 0) num = 0;

    const res = `(${num})`;
    element.innerText = res;
  }

  /**
   * @param {string} paramValue - the url param value
   * @returns {FacetRemove | undefined}
   */
  createProductTypeFacetRemove(paramValue) {
    const filterInputName = 'filter.p.m.custom.product_type';
    const template = document.getElementById('producttype-facetremove');
    if (!template) {
      const err = new Error(
        '<template> for creating <facet-remove> not found.'
      );
      console.error(err);
      return;
    }
    const fragment = document.importNode(template.content, true);
    const facet = fragment.firstElementChild;

    const urlRemove = this.createUrlRemove(filterInputName, paramValue);
    const nameLabel = facet.getAttribute('data-name-label');
    const textLabel = `${nameLabel}: ${paramValue}`;

    facet.setAttribute('data-param-value', paramValue);
    facet.setAttribute('data-value-label', paramValue);
    facet.setAttribute('data-url-remove', urlRemove);

    const link = facet.querySelector('[data-link]');
    if (link) {
      link.setAttribute('href', urlRemove);
    } else {
      const err = new Error(
        '<a> element not found for creating new <facet-remove>',
      );
      console.error(err);
      return;
    }

    const textBox = facet.querySelector('[data-label-text]');
    if (textBox) {
      textBox.textContent = textLabel;
    } else {
      const err = new Error(
        'Unable to inject text content into new <facet-remove>',
      );
      console.error(err);
      return;
    }

    return facet;
  }

  /** Create a new url without the paramName and paramVal
   * @param {string} paramName
   * @param {string} paramVal
   * @returns {string}
  */
  createUrlRemove(paramName, paramVal) {
    const params = new URLSearchParams(window.location.search);
    params.delete(paramName, paramVal);
    const newUrl = (
      params.size > 0
        ? `${window.location.pathname}?${params.toString()}`
        : `${window.location.pathname}`
    );
    return newUrl;
  }

  /**
   * @param {string[]} urlParamVals
   * @param {NodeListOf<FacetRemove>} activeFilters
   * @returns {ProductTypeDiff[]}
   */
  diffProductTypeFilters(urlParamVals, activeFilters) {

    /** @type {ProductTypeDiff[]} */
    const diffs = [];

    if (!urlParamVals.length && !activeFilters.length) {
      return diffs;
    }

    /** @type {string[]} */
    const filterParamAttrs = [];
    activeFilters.forEach((filter) => {
      const filterParamVal = filter.getAttribute('data-param-value');
      if (!filterParamVal) {
        const err = new Error('[data-param-value] is null');
        console.error(err, filter);
        return;
      }
      filterParamAttrs.push(filterParamVal);
    });

    activeFilters.forEach((filter) => {
      const filterParamVal = filter.getAttribute('data-param-value');
      if (!filterParamVal) {
        const err = new Error('[data-param-value] is null');
        console.error(err, filter);
      }
      if (!urlParamVals.includes(filterParamVal)) {
        /** @type {ProductTypeDiffRemove} */
        const diff = {
          action: 'remove',
          filter: filter,
        };
        diffs.push(diff);
      }
    });

    urlParamVals.forEach((urlParamVal) => {
      if (!filterParamAttrs.includes(urlParamVal)) {
        /** @type {ProductTypeDiffAdd} */
        const diff = {
          action: 'add',
          paramValue: urlParamVal,
        };
        diffs.push(diff);
      }
    });

    return diffs;
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {

  constructor() {
    super();
  }

  connectedCallback() {
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    if (event.currentTarget.hasAttribute('data-full-reload')) {
      event.preventDefault();
      window.location.href = event.currentTarget.href;
      return;
    }
    event.preventDefault();    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
